import { apiFetch, apiFetchWithRetry } from '@/lib/apiFetch';
import type { SearchContextSnapshot } from '@/types/searchResults';

export interface SubjectInfo {
  id: string;
  name: string;
}

export interface ParsedQueryIntent {
  subject: string | null;
  gradeBand: string | null;
  targetExam: string | null;
  curriculum: string | null;
  remainder: string | null;
}

export interface TeacherSearchResult {
  teacherProfileId: string;
  handle: string | null;
  fullName: string;
  profilePictureUrl: string | null;
  locationDisplayName: string | null;
  minPrice: number | null;
  maxPrice: number | null;
  /** Currency minPrice/maxPrice are denominated in — server-resolved, never client-supplied */
  currency: string | null;
  trialOffered: boolean;
  offeringTypes: string[];
  gradeBands: string[];
  subjects: SubjectInfo[];
  distanceKm: number | null;
  /** 0–100 for authenticated students with a saved goal profile; null for anonymous */
  matchScore: number | null;
  /** STRONG | GOOD | PARTIAL | WEAK; null when matchScore is null */
  matchBand: string | null;
  /** A bucket code (LESS_THAN_1 | ONE_TO_3 | THREE_TO_5 | FIVE_TO_10 | MORE_THAN_10), not a number; null if unset */
  yearsTeachingExperience: string | null;
  ratingAvg: number | null;
  ratingCount: number;
  /** Teacher's active subscription tier; null when the teacher has no active subscription */
  teacherTier: 'SILVER' | 'GOLD' | 'PLATINUM' | null;
}

export interface TeacherSearchPageResponse {
  content: TeacherSearchResult[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
  parsedIntent?: ParsedQueryIntent | null;
  /** True when no exact-match results existed and closest-fit teachers are returned instead */
  fallbackMode: boolean;
  /** Populated when fallbackMode=true — teachers that partially match the query */
  closestFits: TeacherSearchResult[] | null;
  /** The subject id actually applied to this query (parsed-and-resolved or explicit); absent if none identified */
  resolvedSubjectId?: string | null;
}

export interface GeoDefaultsResponse {
  country: string | null;
  currency: string;
  curricula: string[];
  languages: string[];
  minPrice: number;
  maxPrice: number;
  mode: string;
}

export interface TeacherSearchParams {
  q?: string;
  page?: number;
  size?: number;
  latitude?: number;
  longitude?: number;
  radiusKm?: number;
  subjectId?: string;
  offeringType?: string;
  /** Multi-value — OR semantics: results must match at least one of the selected grade bands */
  gradeBand?: string[];
  language?: string[];
  curriculum?: string[];
  availabilityTimeOfDay?: string;
  trialSessionAvailable?: boolean;
  targetExamId?: string;
  topicKeywords?: string;
  // No `currency` field — the backend no longer accepts client-supplied currency
  // (closes a manipulation vector; see ADR 067). Currency is always resolved
  // server-side from the authenticated student's profile or the geo header.
  priceMin?: number;
  priceMax?: number;
  sortBy?: string;
  /** Optional Cognito access token — passed as Authorization header when present */
  accessToken?: string;
}

export async function searchTeachers(params: TeacherSearchParams): Promise<TeacherSearchPageResponse> {
  const sp = new URLSearchParams();
  if (params.q) sp.set('q', params.q);
  if (params.latitude != null) sp.set('latitude', String(params.latitude));
  if (params.longitude != null) sp.set('longitude', String(params.longitude));
  if (params.radiusKm != null) sp.set('radiusKm', String(params.radiusKm));
  if (params.subjectId) sp.set('subjectId', params.subjectId);
  if (params.offeringType) sp.set('offeringType', params.offeringType);
  params.gradeBand?.forEach(v => sp.append('gradeBand', v));
  params.language?.forEach(v => sp.append('language', v));
  params.curriculum?.forEach(v => sp.append('curriculum', v));
  if (params.availabilityTimeOfDay) sp.set('availabilityTimeOfDay', params.availabilityTimeOfDay);
  if (params.trialSessionAvailable != null) sp.set('trialSessionAvailable', String(params.trialSessionAvailable));
  if (params.targetExamId) sp.set('targetExamId', params.targetExamId);
  if (params.topicKeywords) sp.set('topicKeywords', params.topicKeywords);
  if (params.priceMin != null) sp.set('priceMin', String(params.priceMin));
  if (params.priceMax != null) sp.set('priceMax', String(params.priceMax));
  if (params.sortBy) sp.set('sortBy', params.sortBy);
  sp.set('page', String(params.page ?? 0));
  sp.set('size', String(params.size ?? 20));

  const headers: Record<string, string> = {};
  if (params.accessToken) {
    headers['Authorization'] = `Bearer ${params.accessToken}`;
  }

  const res = await apiFetchWithRetry(
    `/api/v1/marketplace/search/teachers?${sp.toString()}`,
    headers ? { headers } : undefined,
  );
  if (!res.ok) throw new Error(`Teacher search failed: ${res.status}`);
  return res.json();
}

/**
 * accessToken is optional — when present, an authenticated student's
 * persisted profile currency overrides their physical location (see ADR 067).
 * Anonymous callers omit it and get the existing header-based resolution.
 */
export async function getGeoDefaults(accessToken?: string | null): Promise<GeoDefaultsResponse> {
  const headers: Record<string, string> = {};
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

  const res = await apiFetch('/api/v1/marketplace/search/geo-defaults', { headers });
  if (!res.ok) throw new Error(`Geo defaults fetch failed: ${res.status}`);
  return res.json();
}

export interface MatchScoreDimension {
  key: string;
  label: string;
  points: number;
  max: number;
  result: 'MATCH' | 'PARTIAL' | 'NO_MATCH' | 'NOT_SET' | 'WITHHELD';
  detail: string;
}

export interface MatchScoreBreakdown {
  total: number;
  maxTotal: number;
  band: string;
  dimensions: MatchScoreDimension[];
  nonScored: MatchScoreDimension[];
}

export async function getTeacherMatchScore(
  handle: string,
  accessToken: string | null | undefined,
  ctx?: SearchContextSnapshot | null,
): Promise<MatchScoreBreakdown> {
  const params = new URLSearchParams();
  if (ctx?.subject)             params.set('subject', ctx.subject);
  if (ctx?.gradeBand)           params.set('gradeBand', ctx.gradeBand);
  if (ctx?.curriculum)          params.set('curriculum', ctx.curriculum);
  if (ctx?.budgetMax != null)   params.set('budgetMax', String(ctx.budgetMax));
  if (ctx?.examId)              params.set('examId', ctx.examId);
  if (ctx?.language)            params.set('language', ctx.language);

  const qs = params.toString();
  const url = `/api/v1/marketplace/search/teachers/${encodeURIComponent(handle)}/match-score${qs ? '?' + qs : ''}`;
  const headers: Record<string, string> = {};
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

  const res = await apiFetch(url, { headers });
  if (!res.ok) throw new Error(`Match score fetch failed: ${res.status}`);
  return res.json();
}
